<template>
  <div v-if="contract">
    <PageHeader :title="contract.title" showBack>
      <template #actions>
        <b-button v-if="contract.status === 'draft' || contract.status === 'send_failed'" variant="primary" @click="handleSend" :disabled="isSending" class="mr-2">
          <i class="material-icons mr-1" style="font-size: 16px; vertical-align: middle;">send</i>
          {{ isSending ? 'Sending...' : 'Send to Signer' }}
        </b-button>
        <b-button v-if="contract.signed_pdf" variant="outline-primary" :href="contract.signed_pdf" target="_blank" class="mr-2">
          <i class="material-icons mr-1" style="font-size: 16px; vertical-align: middle;">picture_as_pdf</i>
          Signed PDF
        </b-button>
        <b-button v-else-if="contract.pdf_url" variant="outline-primary" :href="contract.pdf_url" target="_blank" class="mr-2">
          <i class="material-icons mr-1" style="font-size: 16px; vertical-align: middle;">picture_as_pdf</i>
          Download PDF
        </b-button>
        <b-button v-if="contract.status !== 'signed' && contract.status !== 'voided'" variant="outline-danger" @click="handleVoid">
          Void
        </b-button>
      </template>
    </PageHeader>

    <!-- Status & Info Row -->
    <b-row class="mb-3">
      <b-col lg="8">
        <b-card no-body>
          <b-card-body>
            <div class="d-flex flex-wrap align-items-start justify-content-between">
              <!-- Left: status + contact -->
              <div class="mb-2">
                <span class="status-badge" :class="contract.status">{{ contract.status }}</span>
                <span v-if="contract.is_pdf_based" class="ml-2 status-badge" style="background-color: #6f42c1; color: #fff;">PDF</span>
              </div>
              <!-- Right: dates -->
              <div class="text-right text-muted small">
                <div>Created {{ formatDate(contract.created_at) }}</div>
                <div v-if="contract.expires_at">Expires {{ formatDate(contract.expires_at) }}</div>
              </div>
            </div>

            <hr class="my-3" />

            <b-row>
              <b-col sm="6" md="3">
                <div class="detail-label">Contact</div>
                <div class="detail-value">{{ contract.contact_name }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="detail-label">Template</div>
                <div class="detail-value">{{ contract.template_name || 'None' }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="detail-label">Sent</div>
                <div class="detail-value">{{ contract.sent_at ? formatDate(contract.sent_at) : '—' }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="detail-label">Signed</div>
                <div class="detail-value">{{ contract.signed_at ? formatDate(contract.signed_at) : '—' }}</div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col lg="4">
        <b-card no-body class="h-100">
          <b-card-body>
            <div class="detail-label mb-2">Signing Link</div>
            <div v-if="contract.signing_token">
              <div class="d-flex align-items-center mb-2">
                <b-form-input :value="signingUrl" readonly size="sm" class="mr-2 signing-url-input" />
                <b-button size="sm" variant="primary" @click="copySigningUrl" title="Copy link" class="flex-shrink-0">
                  <i class="material-icons" style="font-size: 16px;">content_copy</i>
                </b-button>
              </div>
              <a :href="signingUrl" target="_blank" class="small">
                <i class="material-icons mr-1" style="font-size: 14px; vertical-align: middle;">open_in_new</i>Open signing page
              </a>
            </div>
            <div v-else class="text-muted small">No signing token available</div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Timeline -->
    <b-row class="mb-3" v-if="auditLogs.length">
      <b-col>
        <b-card no-body>
          <b-card-body class="pb-2">
            <h6 class="mb-3 font-weight-bold">Activity Timeline</h6>
            <div class="timeline">
              <div v-for="log in auditLogs" :key="log.id" class="timeline-item">
                <div class="timeline-dot" :class="timelineDotClass(log.event)"></div>
                <div class="timeline-content">
                  <span class="status-badge" :class="log.event">{{ log.event }}</span>
                  <span class="text-muted small ml-2">{{ formatDate(log.created_at) }}</span>
                  <span v-if="log.ip_address" class="text-muted small ml-2">IP: {{ log.ip_address }}</span>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Contract Content -->
    <b-row>
      <b-col>
        <b-card no-body>
          <b-card-header class="bg-white d-flex align-items-center justify-content-between">
            <h6 class="mb-0 font-weight-bold">Contract Content</h6>
            <span v-if="contract.is_pdf_based" class="text-muted small">PDF-based contract</span>
          </b-card-header>
          <b-card-body>
            <div v-if="contract.content_html" v-safe-html="contract.content_html" class="contract-content"></div>
            <div v-else class="text-center text-muted py-4">
              <i class="material-icons mb-2" style="font-size: 48px; opacity: 0.3;">description</i>
              <p class="mb-0">No content to display</p>
              <p v-if="contract.is_pdf_based" class="small">This is a PDF-based contract. Use the Download button above to view the PDF.</p>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Signature Details (if signed) -->
    <b-row v-if="contract.signature" class="mt-3">
      <b-col>
        <b-card no-body>
          <b-card-header class="bg-white">
            <h6 class="mb-0 font-weight-bold">Signature</h6>
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col sm="6" md="3">
                <div class="detail-label">Signer Name</div>
                <div class="detail-value">{{ contract.signature.signer_name }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="detail-label">Signer Email</div>
                <div class="detail-value">{{ contract.signature.signer_email }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="detail-label">Type</div>
                <div class="detail-value text-capitalize">{{ contract.signature.signature_type }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="detail-label">Signed At</div>
                <div class="detail-value">{{ formatDate(contract.signature.signed_at) }}</div>
              </b-col>
            </b-row>
            <div v-if="contract.signature.signature_data && contract.signature.signature_type === 'drawn'" class="mt-3">
              <div class="detail-label mb-1">Signature Image</div>
              <div class="signature-preview">
                <img :src="contract.signature.signature_data" alt="Signature" />
              </div>
            </div>
            <div v-else-if="contract.signature.signature_data && contract.signature.signature_type === 'type'" class="mt-3">
              <div class="detail-label mb-1">Typed Signature</div>
              <div class="typed-signature-preview">{{ contract.signature.signature_data }}</div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
  <div v-else class="text-center py-5"><b-spinner /></div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';

const SIGN_URL = process.env.VUE_APP_SIGN_URL || 'http://localhost:8084';

export default {
  name: 'ContractDetail',
  components: { PageHeader },
  data() { return { contract: null, auditLogs: [], isSending: false }; },
  computed: {
    signingUrl() {
      if (!this.contract || !this.contract.signing_token) return '';
      if (SIGN_URL) return `${SIGN_URL}/sign/${this.contract.signing_token}`;
      return `${window.location.origin}/sign/${this.contract.signing_token}`;
    },
    templatePdfUrl() {
      return this.contract?.template_pdf_file || null;
    },
  },
  methods: {
    ...mapActions('contract', ['getContract', 'sendContract', 'voidContract', 'getContractAudit']),
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    timelineDotClass(event) {
      const map = { signed: 'dot-success', viewed: 'dot-primary', sent: 'dot-info', send_failed: 'dot-danger', voided: 'dot-danger', expired: 'dot-muted', created: 'dot-muted' };
      return map[event] || 'dot-muted';
    },
    async loadContract() {
      try {
        const [cRes, aRes] = await Promise.all([
          this.getContract(this.$route.params.id),
          this.getContractAudit(this.$route.params.id),
        ]);
        this.contract = cRes.data;
        this.auditLogs = aRes.data || [];
      } catch (err) { this.makeToast('danger', 'Failed to load'); }
    },
    async handleSend() {
      this.isSending = true;
      try { await this.sendContract(this.$route.params.id); this.makeToast('success', 'Contract sent'); this.loadContract(); }
      catch (err) { this.makeToast('danger', 'Send failed'); }
      finally { this.isSending = false; }
    },
    copySigningUrl() {
      navigator.clipboard.writeText(this.signingUrl).then(() => {
        this.makeToast('success', 'Signing link copied to clipboard');
      });
    },
    async handleVoid() {
      if (!confirm('Void this contract?')) return;
      try { await this.voidContract(this.$route.params.id); this.makeToast('success', 'Contract voided'); this.loadContract(); }
      catch (err) { this.makeToast('danger', 'Void failed'); }
    },
  },
  created() { this.loadContract(); },
};
</script>

<style scoped>
.detail-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #858796;
  font-weight: 600;
  letter-spacing: 0.03em;
  margin-bottom: 0.2rem;
}
.detail-value {
  font-size: 0.95rem;
  font-weight: 500;
  color: #181818;
}
.signing-url-input {
  font-size: 0.78rem;
  background-color: #f8f9fc;
  border-color: #e3e6f0;
}

/* Timeline */
.timeline {
  position: relative;
  padding-left: 24px;
}
.timeline::before {
  content: '';
  position: absolute;
  left: 7px;
  top: 4px;
  bottom: 4px;
  width: 2px;
  background: #e3e6f0;
}
.timeline-item {
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
}
.timeline-item:last-child { padding-bottom: 0; }
.timeline-dot {
  position: absolute;
  left: -20px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #e3e6f0;
}
.dot-success { background: #1cc88a; box-shadow: 0 0 0 2px #1cc88a; }
.dot-primary { background: var(--primary-color, #4e73df); box-shadow: 0 0 0 2px var(--primary-color, #4e73df); }
.dot-info { background: #36b9cc; box-shadow: 0 0 0 2px #36b9cc; }
.dot-danger { background: #e74a3b; box-shadow: 0 0 0 2px #e74a3b; }
.dot-muted { background: #858796; box-shadow: 0 0 0 2px #858796; }

/* Contract content */
.contract-content {
  line-height: 1.75;
  font-size: 0.95rem;
}

/* PDF preview */
.pdf-preview-frame {
  width: 100%;
  height: 600px;
  border: 1px solid #e3e6f0;
  border-radius: 0.35rem;
}

/* Signature preview */
.signature-preview {
  background: #f8f9fc;
  border: 1px solid #e3e6f0;
  border-radius: 0.35rem;
  padding: 1rem;
  display: inline-block;
}
.signature-preview img {
  max-height: 80px;
}
.typed-signature-preview {
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  line-height: 1.1;
  color: #181818;
  padding: 0.5rem 0;
}
</style>
